import type { McpServiceId } from './types'

export const MCP_CLIENTS = [
  'claude',
  'claudeCode',
  'chatgpt',
  'geminiCli',
  'cursor',
  'vscode',
  'other'
] as const

export type McpClientId = (typeof MCP_CLIENTS)[number]

/** Clients that sign in through Core's OAuth flow; `other` uses a personal token. */
export const OAUTH_CLIENTS: ReadonlySet<McpClientId> = new Set([
  'claude',
  'claudeCode',
  'chatgpt',
  'geminiCli',
  'cursor',
  'vscode'
])

export const PAT_PLACEHOLDER = '<YOUR_PERSONAL_TOKEN>'

/** Keys under `workspace.aiConnectors.connect.steps`. */
export type ClientStepKey =
  | 'claude.s1'
  | 'claude.s2'
  | 'claude.s3'
  | 'chatgpt.s1'
  | 'chatgpt.s2'
  | 'chatgpt.s3'
  | 'claudeCode.s1'
  | 'claudeCode.s2'
  | 'geminiCli.s1'
  | 'geminiCli.s2'
  | 'cursor.s1'
  | 'cursor.s2'
  | 'vscode.s1'
  | 'vscode.s2'
  | 'other.s1'
  | 'other.s2'

export type ClientSnippet = {
  stepKeys: ClientStepKey[]
  /** Text to paste (command or JSON), or null for clients configured in a UI. */
  code: string | null
}

export type SnippetServer = {
  service: McpServiceId
  /** Core's canonical MCP URL (trailing slash, contract A6); used verbatim. */
  url: string
}

type SnippetInput = {
  client: McpClientId
  /** One entry per service; several give one config that adds them all. */
  servers: SnippetServer[]
  token?: string | null
}

export const mcpServerName = (service: McpServiceId) => `skalx-${service}`

const toJson = (value: unknown) => JSON.stringify(value, null, 2)

const byName = <T>(
  servers: SnippetServer[],
  entry: (url: string) => T
): Record<string, T> =>
  Object.fromEntries(
    servers.map(({ service, url }) => [mcpServerName(service), entry(url)])
  )

export const buildClientSnippet = ({
  client,
  servers,
  token
}: SnippetInput): ClientSnippet => {
  switch (client) {
    case 'claude':
      return { stepKeys: ['claude.s1', 'claude.s2', 'claude.s3'], code: null }
    case 'chatgpt':
      return {
        stepKeys: ['chatgpt.s1', 'chatgpt.s2', 'chatgpt.s3'],
        code: null
      }
    case 'claudeCode':
      return {
        stepKeys: ['claudeCode.s1', 'claudeCode.s2'],
        code: servers
          .map(
            ({ service, url }) =>
              `claude mcp add --transport http ${mcpServerName(service)} ${url}`
          )
          .join('\n')
      }
    case 'geminiCli':
      return {
        stepKeys: ['geminiCli.s1', 'geminiCli.s2'],
        code: toJson({
          mcpServers: byName(servers, (url) => ({ httpUrl: url }))
        })
      }
    case 'cursor':
      return {
        stepKeys: ['cursor.s1', 'cursor.s2'],
        code: toJson({ mcpServers: byName(servers, (url) => ({ url })) })
      }
    case 'vscode':
      return {
        stepKeys: ['vscode.s1', 'vscode.s2'],
        code: toJson({
          servers: byName(servers, (url) => ({ type: 'http', url }))
        })
      }
    case 'other':
      return {
        stepKeys: ['other.s1', 'other.s2'],
        code: toJson({
          mcpServers: byName(servers, (url) => ({
            url,
            headers: { Authorization: `Bearer ${token || PAT_PLACEHOLDER}` }
          }))
        })
      }
  }
}
